import { useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  List,
  ListIcon,
  ListItem,
  useDisclosure,
} from '@chakra-ui/react';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import { OBCard } from 'features/Bento/components';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import FloatingCash from './assets/FloatingCash@2x.png';
import Notepad from './assets/Notepad@2x.png';
import MoreDetailsModal from './components/MoreDetailsModal';
import locales from './locales';

interface CapitalDepositGuideProps {
  onContinue: () => void;
}

const renderItems = () => [
  <Box key="about-capital-deposit">
    <OBCard.Illustration src={FloatingCash} />

    <OBCard.Body minHeight="250px" textAlign="center">
      <Heading marginBottom="space-16" size="lg">
        {locales.firstCardHeading}
      </Heading>

      <Text marginBottom="space-16">{locales.firstCardDescription}</Text>
    </OBCard.Body>
  </Box>,

  <Box key="requirements-capital-deposit">
    <OBCard.Illustration src={Notepad} />

    <OBCard.Body minHeight="250px" textAlign="center">
      <Heading marginBottom="space-32" size="lg">
        {locales.secondCardHeading}
      </Heading>

      <List
        color="grey.-1"
        marginBottom="space-20"
        paddingX="space-8"
        spacing="space-16"
        textAlign="left"
      >
        {locales.secondCardDescriptionBullets.map((bullet) => (
          <Grid
            as={ListItem}
            gap="space-12"
            key={bullet}
            templateColumns="auto 1fr"
          >
            <ListIcon
              as={() => (
                <SunshineIcon marginTop="space-4" name="tick" size="icon-13" />
              )}
            />
            <Text>{bullet}</Text>
          </Grid>
        ))}
      </List>
    </OBCard.Body>
  </Box>,
];

const CapitalDepositGuide = asBentoPage<CapitalDepositGuideProps>(
  ({ Layout, onContinue, onPrev }) => {
    const [carouselIndex, setCarouselIndex] = useState(0);
    const detailsModal = useDisclosure();

    const items = renderItems();

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <MoreDetailsModal {...detailsModal} />

          <OBCard flexGrow={1}>
            <OBCard.Carousel
              onChange={(index) => setCarouselIndex(index)}
              onDone={onContinue}
              value={carouselIndex}
            >
              {items}
            </OBCard.Carousel>

            <OBCard.Footer
              bottom="space-24"
              position={{ base: 'sticky', md: 'initial' }}
            >
              <Flex justify="space-between" width="full">
                <Flex alignItems="center" justifyContent="center">
                  <Button
                    onClick={detailsModal.onOpen}
                    variant="inline-primary"
                  >
                    {locales.howItWorksButton}
                  </Button>
                </Flex>

                <Button
                  onClick={
                    carouselIndex >= items.length - 1
                      ? onContinue
                      : () => setCarouselIndex(carouselIndex + 1)
                  }
                >
                  {locales.nextCta}
                </Button>
              </Flex>
            </OBCard.Footer>
          </OBCard>
        </Flex>
      </Layout>
    );
  },
);

export default CapitalDepositGuide;
